import type { z } from "zod";
import { type AccountBalanceTypeSchema, type GclAccount, type GclTransaction } from "./models.js";

const balanceTypeByPriority: z.infer<typeof AccountBalanceTypeSchema>[] = [
  "interimBooked",
  "closingBooked",
  "openingBooked",
  "previouslyClosedBooked",
  "expected",
];

export const getGclBalance = (
  account: Pick<GclAccount, "balances" | "metadata" | "pendingTransactions">,
): NonNullable<GclAccount["balances"]>[0] => {
  if (!account.balances?.[0])
    throw new Error("No balance found for GCL account " + JSON.stringify(account));

  let balance = account.balances[0];
  for (const balanceType of balanceTypeByPriority) {
    const _balance = account.balances.find((b) => b.balanceType === balanceType);
    if (_balance) {
      balance = _balance;
      break;
    }
  }

  // If balance is of type expected, we need to subtract the pending transactions' amount
  let adjustedBalanceAmount;
  if (balance.balanceType === "expected") {
    let pendingAmount = 0;
    for (const pendingTransaction of account.pendingTransactions || []) {
      if (pendingTransaction.transactionAmount.currency !== balance.balanceAmount.currency)
        continue;
      pendingAmount += parseFloat(pendingTransaction.transactionAmount.amount);
    }
    adjustedBalanceAmount = (parseFloat(balance.balanceAmount.amount) - pendingAmount).toFixed(2);
  }

  return {
    balanceAmount: {
      amount: adjustedBalanceAmount ?? balance.balanceAmount.amount,
      currency: balance.balanceAmount.currency,
    },
    balanceType: balance.balanceType,
  };
};

export const getGclTransactionName = (
  t: GclTransaction,
  account: GclAccount | null | undefined,
) => {
  if (account?.metadata.institution_id.includes("QONTO"))
    return t.creditorName || t.debtorName || "";
  else
    return (
      t.remittanceInformationUnstructured ||
      t.remittanceInformationStructured ||
      t.creditorName ||
      t.debtorName ||
      ""
    );
};
